
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Date} from '../core/Date';
import {FieldModelDate} from '../core/Date';

/**
 * BillingStatisticsRecord struct
 */
class BillingStatisticsRecord {
  /**
   * Initialize struct
   * @param {!Date=} date
   * @param {!number=} transactionTradeVolumeUSDSum
   * @param {!number=} transactionsWithMissedTradeVolumeUSDCount
   * @constructor
   */
  constructor (date = new Date(), transactionTradeVolumeUSDSum = 0.0, transactionsWithMissedTradeVolumeUSDCount = new UInt64(0, 0)) {
    this.Date = date
    this.TransactionTradeVolumeUSDSum = transactionTradeVolumeUSDSum
    this.TransactionsWithMissedTradeVolumeUSDCount = transactionsWithMissedTradeVolumeUSDCount
  }

  /**
   * Copy struct (shallow copy)
   * @this {!BillingStatisticsRecord}
   * @param {!BillingStatisticsRecord} other Other struct
   * @returns {!BillingStatisticsRecord} This struct
   */
  copy (other) {
    if (other.Date != null) {
      this.Date = Date.fromObject(other.Date)
    } else {
      this.Date = null
    }
    if (other.TransactionTradeVolumeUSDSum != null) {
      this.TransactionTradeVolumeUSDSum = other.TransactionTradeVolumeUSDSum
    } else {
      this.TransactionTradeVolumeUSDSum = null
    }
    if (other.TransactionsWithMissedTradeVolumeUSDCount != null) {
      this.TransactionsWithMissedTradeVolumeUSDCount = UInt64.fromNumber(other.TransactionsWithMissedTradeVolumeUSDCount)
    } else {
      this.TransactionsWithMissedTradeVolumeUSDCount = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!BillingStatisticsRecord}
   * @returns {!BillingStatisticsRecord} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new BillingStatisticsRecordModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new BillingStatisticsRecordModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!BillingStatisticsRecord}
   * @param {!BillingStatisticsRecord} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof BillingStatisticsRecord)) {
      throw new TypeError('Instance of BillingStatisticsRecord is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!BillingStatisticsRecord}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Date: ((this.Date != null) ? this.Date : null),
      TransactionTradeVolumeUSDSum: ((this.TransactionTradeVolumeUSDSum != null) ? this.TransactionTradeVolumeUSDSum : null),
      TransactionsWithMissedTradeVolumeUSDCount: ((this.TransactionsWithMissedTradeVolumeUSDCount != null) ? this.TransactionsWithMissedTradeVolumeUSDCount.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return BillingStatisticsRecord.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!BillingStatisticsRecord} other Object value
   * @returns {!BillingStatisticsRecord} Created struct
   */
  static fromObject (other) {
    return new BillingStatisticsRecord().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!BillingStatisticsRecord}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return BillingStatisticsRecord.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!BillingStatisticsRecord}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 13018
  }
}

export { BillingStatisticsRecord };

/**
 * Fast Binary Encoding BillingStatisticsRecord field model
 */
class FieldModelBillingStatisticsRecord extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Date = new FieldModelDate(buffer, 4 + 4)
    this.TransactionTradeVolumeUSDSum = new fbe.FieldModelDouble(buffer, this.Date.fbeOffset + this.Date.fbeSize)
    this.TransactionsWithMissedTradeVolumeUSDCount = new fbe.FieldModelUInt64(buffer, this.TransactionTradeVolumeUSDSum.fbeOffset + this.TransactionTradeVolumeUSDSum.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelBillingStatisticsRecord}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelBillingStatisticsRecord}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Date.fbeSize + this.TransactionTradeVolumeUSDSum.fbeSize + this.TransactionsWithMissedTradeVolumeUSDCount.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelBillingStatisticsRecord}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Date.fbeExtra + this.TransactionTradeVolumeUSDSum.fbeExtra + this.TransactionsWithMissedTradeVolumeUSDCount.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelBillingStatisticsRecord}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelBillingStatisticsRecord.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelBillingStatisticsRecord}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 13018
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelBillingStatisticsRecord}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelBillingStatisticsRecord}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Date.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Date.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Date.fbeSize

    if ((fbeCurrentSize + this.TransactionTradeVolumeUSDSum.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.TransactionTradeVolumeUSDSum.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TransactionTradeVolumeUSDSum.fbeSize

    if ((fbeCurrentSize + this.TransactionsWithMissedTradeVolumeUSDCount.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.TransactionsWithMissedTradeVolumeUSDCount.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TransactionsWithMissedTradeVolumeUSDCount.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelBillingStatisticsRecord}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelBillingStatisticsRecord}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelBillingStatisticsRecord}
   * @param {!BillingStatisticsRecord} fbeValue Default value, defaults is new BillingStatisticsRecord()
   * @returns {!BillingStatisticsRecord} BillingStatisticsRecord value
   */
  get (fbeValue = new BillingStatisticsRecord()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelBillingStatisticsRecord}
   * @param {!BillingStatisticsRecord} fbeValue BillingStatisticsRecord value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Date.fbeSize) <= fbeStructSize) {
      fbeValue.Date = this.Date.get()
    } else {
      fbeValue.Date = new Date()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Date.fbeSize

    if ((fbeCurrentSize + this.TransactionTradeVolumeUSDSum.fbeSize) <= fbeStructSize) {
      fbeValue.TransactionTradeVolumeUSDSum = this.TransactionTradeVolumeUSDSum.get()
    } else {
      fbeValue.TransactionTradeVolumeUSDSum = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TransactionTradeVolumeUSDSum.fbeSize

    if ((fbeCurrentSize + this.TransactionsWithMissedTradeVolumeUSDCount.fbeSize) <= fbeStructSize) {
      fbeValue.TransactionsWithMissedTradeVolumeUSDCount = this.TransactionsWithMissedTradeVolumeUSDCount.get()
    } else {
      fbeValue.TransactionsWithMissedTradeVolumeUSDCount = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TransactionsWithMissedTradeVolumeUSDCount.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelBillingStatisticsRecord}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelBillingStatisticsRecord}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelBillingStatisticsRecord}
   * @param {!BillingStatisticsRecord} fbeValue BillingStatisticsRecord value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelBillingStatisticsRecord}
   * @param {BillingStatisticsRecord} fbeValue BillingStatisticsRecord value
   */
  setFields (fbeValue) {
    this.Date.set(fbeValue.Date)
    this.TransactionTradeVolumeUSDSum.set(fbeValue.TransactionTradeVolumeUSDSum)
    this.TransactionsWithMissedTradeVolumeUSDCount.set(fbeValue.TransactionsWithMissedTradeVolumeUSDCount)
  }
}

export { FieldModelBillingStatisticsRecord };

/**
 * Fast Binary Encoding BillingStatisticsRecord model
 */
class BillingStatisticsRecordModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelBillingStatisticsRecord(this.buffer, 4)
  }

  /**
   * Get the BillingStatisticsRecord model
   * @this {!BillingStatisticsRecordModel}
   * @returns {!FieldModelBillingStatisticsRecord} model BillingStatisticsRecord model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!BillingStatisticsRecordModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!BillingStatisticsRecordModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return BillingStatisticsRecordModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!BillingStatisticsRecordModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelBillingStatisticsRecord.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!BillingStatisticsRecordModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!BillingStatisticsRecordModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!BillingStatisticsRecordModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!BillingStatisticsRecordModel}
   * @param {!BillingStatisticsRecord} value BillingStatisticsRecord value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!BillingStatisticsRecordModel}
   * @param {!BillingStatisticsRecord} value BillingStatisticsRecord value, defaults is new BillingStatisticsRecord()
   * @return {!object} Deserialized BillingStatisticsRecord value and its size
   */
  deserialize (value = new BillingStatisticsRecord()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new BillingStatisticsRecord(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new BillingStatisticsRecord(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!BillingStatisticsRecordModel}
   * @param {!number} prev Previous BillingStatisticsRecord model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { BillingStatisticsRecordModel };
